import {
  ABOR_MARKET_ACCOUNT_PERMISSIONS_FILTER,
  useEndpointsContext,
  useGlobalToasts,
  useMarketAccountsContext,
  useObservable,
  useObservableValue,
  useSubscription,
  type ABORMarketAccountApiPermissionFilter,
  type ErrorSubscriptionResponse,
  type IABORMarketAccountApiPermissionFilter,
  type SubscriptionResponse,
} from '@talos/kyoko';
import { createContext, useCallback, useContext, useMemo, useRef, type PropsWithChildren } from 'react';
import { Subject, type Observable } from 'rxjs';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { observeABORMarketAccountPermissionFilters } from './aborMarketAccountPermissionFilters';
import { saveMarketAccountPermissionFilters } from './saveMarketAccountPermissionFilters';

export const ABORMarketAccountPermissionFiltersContext = createContext<
  ABORMarketAccountPermissionFiltersContextProps | undefined
>(undefined);
ABORMarketAccountPermissionFiltersContext.displayName = 'ABORMarketAccountPermissionFiltersContext';

export type ABORMarketAccountPermissionFiltersContextProps = {
  aborMarketAccountFilterIDsBySubAccountName: Map<string, Set<string>> | undefined;
  aborMarketAccountPermissionFiltersByFilterID: Map<string, ABORMarketAccountApiPermissionFilter> | undefined;
  /** False until the permission-filter subscription emits a snapshot. An empty snapshot is loaded. */
  isLoaded: boolean;
  /** True when the subscription failed before any snapshot arrived. */
  hasLoadError: boolean;
  error: Observable<ErrorSubscriptionResponse | undefined>;
  updateABORMarketAccountPermissionFilters: (
    updatedPermissions: ABORMarketAccountApiPermissionFilter[]
  ) => Promise<string[]>;
};

export function useABORMarketAccountPermissionFilters() {
  const context = useContext(ABORMarketAccountPermissionFiltersContext);
  if (context === undefined) {
    throw new Error(
      'Missing ABORMarketAccountPermissionFiltersContext.Provider further up in the tree. Did you forget to add it?'
    );
  }
  return context;
}

type ABORMarketAccountPermissionFilterState = {
  aborMarketAccountPermissionFiltersByFilterID: Map<string, ABORMarketAccountApiPermissionFilter> | undefined;
  aborMarketAccountFilterIDsBySubAccountName: Map<string, Set<string>> | undefined;
};

const ABOR_PERMISSION_FILTERS_UNLOADED: ABORMarketAccountPermissionFilterState = {
  aborMarketAccountPermissionFiltersByFilterID: undefined,
  aborMarketAccountFilterIDsBySubAccountName: undefined,
};

const ABOR_MARKET_ACCOUNT_PERMISSION_REQUEST = {
  name: ABOR_MARKET_ACCOUNT_PERMISSIONS_FILTER,
  tag: 'ABORMarketAccountPermissionFiltersProvider',
  limit: 5000,
};

export const ABORMarketAccountPermissionFiltersProvider = function ABORMarketAccountPermissionFiltersProvider({
  children,
}: PropsWithChildren) {
  const { enablePMSIborAborReport, enablePMSIborAborRecon } = useFeatureFlag();
  const { data: aborMarketAccountPermissionsFilterRawObs, error: aborMarketAccountPermissionsFilterErrorObs } =
    useSubscription<IABORMarketAccountApiPermissionFilter>(
      enablePMSIborAborReport || enablePMSIborAborRecon ? ABOR_MARKET_ACCOUNT_PERMISSION_REQUEST : null
    );
  const subscriptionError = useObservableValue(
    () => aborMarketAccountPermissionsFilterErrorObs,
    [aborMarketAccountPermissionsFilterErrorObs]
  );

  const aborMarketAccountPermissionsFilterUpdatesSubject = useRef(
    new Subject<SubscriptionResponse<ABORMarketAccountApiPermissionFilter, string>>()
  );
  const aborMarketAccountPermissionsFilterUpdatesObs = useObservable<
    SubscriptionResponse<ABORMarketAccountApiPermissionFilter, string>
  >(() => aborMarketAccountPermissionsFilterUpdatesSubject.current.asObservable(), []);

  const { orgApiEndpoint } = useEndpointsContext();
  const { marketAccountDisplayNameByName } = useMarketAccountsContext();
  const { add: addToast } = useGlobalToasts();

  const updateABORMarketAccountPermissionFilters = useCallback(
    async (updatedPermissions: ABORMarketAccountApiPermissionFilter[]): Promise<string[]> => {
      if (!orgApiEndpoint) {
        return [];
      }

      const { applied, failedFilterIDs } = await saveMarketAccountPermissionFilters({
        permissions: updatedPermissions,
        deleteUrl: permission =>
          `${orgApiEndpoint}/permission-filters/abormarketaccounts/${encodeURIComponent(permission.FilterID)}`,
        createUrl: `${orgApiEndpoint}/permission-filters/abormarketaccounts`,
        createBody: permission => ({
          Action: permission.Action,
          Filter: { ABORMarketAccount: permission.Filter.ABORMarketAccount },
          Subject: { SubAccount: permission.Subject.SubAccount },
        }),
        marketAccountName: permission =>
          marketAccountDisplayNameByName.get(permission.Filter.ABORMarketAccount) ||
          permission.Filter.ABORMarketAccount,
        addToast,
      });

      aborMarketAccountPermissionsFilterUpdatesSubject.current.next({
        type: '',
        tag: 'REST_RESPONSE',
        ts: '',
        data: applied,
      });

      return failedFilterIDs;
    },
    [orgApiEndpoint, marketAccountDisplayNameByName, addToast]
  );

  const aborMarketAccountPermissionFiltersObs = useObservableValue(
    () =>
      observeABORMarketAccountPermissionFilters(
        aborMarketAccountPermissionsFilterRawObs,
        aborMarketAccountPermissionsFilterUpdatesObs
      ),
    [aborMarketAccountPermissionsFilterRawObs, aborMarketAccountPermissionsFilterUpdatesObs]
  );

  const { aborMarketAccountFilterIDsBySubAccountName, aborMarketAccountPermissionFiltersByFilterID } =
    aborMarketAccountPermissionFiltersObs ?? ABOR_PERMISSION_FILTERS_UNLOADED;
  const isLoaded = aborMarketAccountPermissionFiltersObs != null;
  const hasLoadError = !isLoaded && subscriptionError != null;

  const value = useMemo(
    () => ({
      aborMarketAccountPermissionFiltersByFilterID,
      aborMarketAccountFilterIDsBySubAccountName,
      isLoaded,
      hasLoadError,
      error: aborMarketAccountPermissionsFilterErrorObs,
      updateABORMarketAccountPermissionFilters,
    }),
    [
      aborMarketAccountPermissionFiltersByFilterID,
      aborMarketAccountFilterIDsBySubAccountName,
      isLoaded,
      hasLoadError,
      aborMarketAccountPermissionsFilterErrorObs,
      updateABORMarketAccountPermissionFilters,
    ]
  );

  return (
    <ABORMarketAccountPermissionFiltersContext.Provider value={value}>
      {children}
    </ABORMarketAccountPermissionFiltersContext.Provider>
  );
};
